import { resolveParameters } from "@repo/domain";
import { readPerformanceMeasureDefinitionRow } from "../fhir/questionnaire-definition-storage";
/**
 * What a revision of each kind is worth diffing — one allow-list per kind.
 *
 * The list is deliberate, not a projection dumped whole. A DTO carries fields
 * that differ between every pair of revisions by construction — the authoring
 * date, the version, the resource id — and diffing those reports "Authored At
 * changed" on every revision ever made, which is true and useless. Only the
 * domain fields a person changed belong here.
 *
 * One implementation for all eight kinds because two readers need it: the
 * version history of the definition being viewed, and a change point naming a
 * revision of something UNDERNEATH it. Those must read identically — a child's
 * "Duration 45 → 60" cannot be worded differently from the root's — and the only
 * way to guarantee that is for both to call this.
 */

import type {
  ActivityDefinition,
  DeviceDefinition,
  ObservationDefinition,
  PlanDefinition,
  Questionnaire,
} from "@medplum/fhirtypes";
import { projectDeviceDefinitionRow } from "./device-definition-projection";
import { projectDiagnosticDefinitionRow } from "./diagnostic-definition-projection";
import { projectExamDefinition } from "./exam-definition";
import { projectFindingDefinition } from "./finding-definition";
import { projectProgramDefinitionRow } from "./program-definition-projection";
import { projectProtocolDefinitionRow } from "./protocol-definition-projection";
import { projectQuestionnaireDefinition } from "./questionnaire-definition";
import { projectTreatmentDefinitionRow } from "./treatment-definition-projection";

export type RevisionSnapshotKind =
  | "diagnostic"
  | "treatment"
  | "protocol"
  | "program"
  | "device"
  | "finding"
  | "exam"
  | "questionnaire"
  | "performance-measure";

type AnyCatalogResource =
  | ActivityDefinition
  | PlanDefinition
  | Questionnaire
  | DeviceDefinition
  | ObservationDefinition;

export function revisionSnapshot(kind: RevisionSnapshotKind, resource: AnyCatalogResource): unknown {
  switch (kind) {
    case "diagnostic":
      return snapshotOfDiagnostic(projectDiagnosticDefinitionRow(resource as ActivityDefinition));
    case "treatment":
      return snapshotOfTreatment(projectTreatmentDefinitionRow(resource as ActivityDefinition));
    case "protocol":
      return snapshotOfProtocol(projectProtocolDefinitionRow(resource as PlanDefinition));
    case "program":
      return snapshotOfProgram(projectProgramDefinitionRow(resource as PlanDefinition));
    case "device":
      return snapshotOfDevice(projectDeviceDefinitionRow(resource as DeviceDefinition));
    case "finding":
      return snapshotOfFinding(projectFindingDefinition(resource as ObservationDefinition));
    case "exam":
      return snapshotOfExam(projectExamDefinition(resource as ActivityDefinition));
    case "performance-measure":
      return snapshotOfQuestionnaire(readPerformanceMeasureDefinitionRow(resource as Questionnaire));
    case "questionnaire":
      return snapshotOfQuestionnaire(projectQuestionnaireDefinition(resource as Questionnaire));
  }
}

// The allow-lists themselves, one per kind, over the projected DTO.
//
// They take a DTO rather than a resource because two callers already hold one:
// the device and finding version histories project before they diff. Splitting
// here keeps a single list per kind either way — the alternative was those two
// carrying their own copy, which is how the change points ended up reporting
// "Authored At changed" and nothing else.

type Dto<F extends (...args: never[]) => unknown> = ReturnType<F>;

function snapshotOfDiagnostic(definition: Dto<typeof projectDiagnosticDefinitionRow>) {
  return {
    name: definition.name,
    description: definition.description,
    availability: definition.availability,
    standardObservations: definition.standardObservations,
    procedureNoteLabel: definition.procedureNoteLabel,
    observations: definition.observations,
    parameters: resolveParameters(definition.parameters),
    requirements: definition.requirements,
  };
}

export function snapshotOfTreatment(definition: Dto<typeof projectTreatmentDefinitionRow>) {
  return {
    name: definition.name,
    description: definition.description,
    availability: definition.availability,
    procedureNoteLabel: definition.procedureNoteLabel,
    standardObservations: definition.standardObservations,
    observations: definition.chartedObservations,
    parameters: resolveParameters(definition.parameters),
    deviceRequirements: definition.deviceRequirements,
    locationRequirement: definition.locationRequirement,
  };
}

function snapshotOfProtocol(definition: Dto<typeof projectProtocolDefinitionRow>) {
  return {
    name: definition.name,
    description: definition.description,
    availability: definition.availability,
    envelopeMinutes: definition.envelopeMinutes,
    steps: definition.steps,
    exams: definition.examDefinitionUrls,
  };
}

function snapshotOfProgram(definition: Dto<typeof projectProgramDefinitionRow>) {
  return {
    name: definition.name,
    description: definition.description,
    availability: definition.availability,
    days: definition.days,
  };
}

export function snapshotOfDevice(definition: Dto<typeof projectDeviceDefinitionRow>) {
  return {
    name: definition.name,
    description: definition.description,
    availability: definition.availability,
    manufacturer: definition.manufacturer,
    modelNumber: definition.modelNumber,
    parameters: definition.parameters,
  };
}

export function snapshotOfFinding(definition: Dto<typeof projectFindingDefinition>) {
  return {
    kind: definition.kind,
    ...(definition.kind === "Measured" ? { components: definition.components } : {}),
    name: definition.name,
    description: definition.description,
    availability: definition.availability,
    ...(definition.kind === "Choice"
      ? {
          cardinality: definition.cardinality,
          offerOther: definition.offerOther,
          options: definition.options,
        }
      : {}),
  };
}

function snapshotOfExam(definition: Dto<typeof projectExamDefinition>) {
  return {
    name: definition.name,
    description: definition.description,
    availability: definition.availability,
    examNoteLabel: definition.examNoteLabel,
    sections: definition.sections,
  };
}

function snapshotOfQuestionnaire(definition: Dto<typeof projectQuestionnaireDefinition>) {
  return {
    name: definition.name,
    description: definition.description,
    availability: definition.availability,
    sections: definition.sections,
  };
}
